import type { ObjektDTO, SolrResultItemDTO } from '~/types/TypesFromPortalBackend'
import type { VacationRental } from '~/types/json-ld/VacationRental'

const removeSpecialChars = (desc: string): string => {
  let descFiltered: string
  descFiltered = desc.replace(/['"]+/g, '')
  descFiltered = descFiltered.replace(/(\r\n|\n|\r)/gm, '')
  return descFiltered
}

const getAdditionalType = (haustyp: string): string => {
  if (haustyp === 'wohnung') {
    return 'Apartment'
  }
  if (haustyp === 'haus') {
    return 'House'
  }
  return 'VacationRental'
}

const amenitiesMap = {
  Klimaanlage: 'ac',
  Balkon: 'balcony',
  Kleinkindgerecht: 'childFriendly',
  Fahrstuhl: 'elevator',
  Kamin: 'fireplace',
  Fitnessgeräte: 'gymFitnessEquipment',
  Whirlpool: 'hotTub',
  Onlinebuchung: 'instantBookable',
  Mikrowelle: 'microwave',
  Grill: 'outdoorGrill',
  Backofen: 'ovenStove',
  Terrasse: 'patio',
  'Hunde erlaubt': 'petsAllowed',
  Pool: 'pool',
  TV: 'tv',
  'Internet mit WLAN': 'wifi',
  Barrierefrei: 'wheelchairAccessible'
}

const getAmenityFeatures = (keyfacts: object[]): object[] => {
  if (!keyfacts) {
    return []
  }

  const filteredAmenities = keyfacts
    .map(amenity => amenitiesMap[amenity.title])
    .filter(value => value !== undefined && value !== null)

  return filteredAmenities.map(amenity => ({
    '@type': 'LocationFeatureSpecification',
    name: amenity,
    value: true
  }))
}

const getReview = (review: object): object|null => {
  if (!review.author?.nickname || !review.author?.date) {
    return null
  }

  const formattedDate = new ExtendedDate(review.author.date).ymd
  const formattedDateReference = new ExtendedDate(review.stay.from).ymd

  const reviewRatingValue = review.rating.total
  const reviewRating = reviewRatingValue > 0
    ? {
        '@type': 'Rating',
        ratingValue: reviewRatingValue,
        bestRating: reviewRatingValue
      }
    : undefined

  return {
    '@type': 'Review',
    ...(reviewRating && { reviewRating }),
    author: {
      '@type': 'Person',
      name: review.author.nickname
    },
    datePublished: formattedDate,
    contentReferenceTime: formattedDateReference,
    text: review.text
  }
}

const getReviews = (reviews: object[]): object[] => {
  return reviews.map((review) => {
    return getReview(review)
  }).filter(review => review !== null)
}

const getAggregateRating = (object: SolrResultItemDTO|ObjektDTO): object | undefined => {
  if (object?.ratings?.total > 0) {
    return {
      bestRating: 5,
      worstRating: 1,
      ratingValue: object.ratings.total,
      ratingCount: object.ratings.count
    }
  }

  if (object?.bewertung_durchschnitt > 0) {
    return {
      bestRating: 5,
      worstRating: 1,
      ratingValue: object.bewertung_durchschnitt,
      reviewCount: object?.bewertung_count
    }
  }

  if (object?.bewertung > 0) {
    return {
      bestRating: 5,
      worstRating: 1,
      ratingValue: object.bewertung,
      reviewCount: object?.bewertungen
    }
  }

  return undefined
}

const getAssembledObject = (object: SolrResultItemDTO|ObjektDTO): VacationRental => {
  let images = object?.bilder_neu?.length
    ? object.bilder_neu.map((picture: string) => picture)
    : object?.pictures?.map((picture: any) => picture.urls.normal) || []

  images = images.slice(0, 1)
  const latLonFromPoiString = typeof object?.poi === 'string' ? object.poi.split(',') : []
  const latitude = latLonFromPoiString[0] || object?.poi?.lat
  const longitude = latLonFromPoiString[1] || object?.poi?.lon
  const aggregateRating = getAggregateRating(object)
  const hausname = (() => {
    if (object.generated_hausname) {
      return object.generated_hausname
    }

    return object.hausname
  })()
  return {
    '@type': 'VacationRental',
    additionalType: getAdditionalType(object?.haustyp),
    brand: {
      '@type': 'Brand',
      name: object?.url?.portal ?? ''
    },
    containsPlace: {
      '@type': 'Accommodation',
      additionalType: 'EntirePlace',
      bed: [
        {
          '@type': 'BedDetails',
          numberOfBeds: object?.beds?.doppelbetten ?? 0,
          typeOfBed: 'Queen'
        },
        {
          '@type': 'BedDetails',
          numberOfBeds: object?.beds?.einzelbetten ?? 0,
          typeOfBed: 'Single'
        }
      ],
      occupancy: {
        '@type': 'QuantitativeValue',
        value: object?.personen
      },
      amenityFeature: getAmenityFeatures(object?.amenities?.keyfacts),
      floorSize: {
        '@type': 'QuantitativeValue',
        value: object?.wohnflaeche ?? 0,
        unitCode: 'MTK'
      },
      numberOfBathroomsTotal: object?.rooms?.badezimmer ?? 0,
      numberOfBedrooms: object?.rooms?.schlafzimmer ?? object?.schlafzimmer ?? 0,
      numberOfRooms: object?.rooms?.raeume ?? 0
    },
    identifier: object?.nr,
    latitude,
    longitude,
    name: removeSpecialChars(hausname),
    address: {
      addressLocality: object?.address?.ort ?? object?.ortsname ?? '',
      postalCode: object?.address?.plz ?? ''
    },
    ...(aggregateRating && { aggregateRating: { ...aggregateRating } }),
    ...(typeof object?.url === 'string' && {
      url: object?.url
    }),
    image: images,
    description: removeSpecialChars(object?.texts?.beschreibung_kurz?.value ?? ''),
    review: getReviews(object?.reviews ?? [])
  }
}

export default getAssembledObject
